import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Badge,
  Button,
  EditSquareIcon,
  ErrorFillIcon,
  Modal,
  Text,
  type TypeBadgeProps,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { useShallow } from 'zustand/react/shallow';

import { useGetActiveSkusSkuCodeQuery, useGetSkusSkuCodeQuery, useProductModalOptions } from '~/hooks/product';
import { PRODUCT_MODAL_INITIAL_VALUE, useProductStore } from '~/store';

import { PATH, TOAST_MESSAGE } from '~/constants';

import {
  ProductDetailsModalDefaultSection,
  ProductDetailsModalProductInfoSection,
  ProductDetailsModalStockInfoSection,
} from '.';

import type { TypeProductModalInput, TypeSkuDetailData, TypeSkuDetailDataWithIsTemp } from '~/types/product.types';

import styles from './ProductDetailsModal.module.scss';

type TypeProductDetailsModal = {
  skuCode: string;
  isActiveSku?: boolean;
  open: boolean;
  onCancel: () => void | Promise<void>;
  onConfirm: () => void | Promise<void>;
};

const ProductDetailsModal = ({ skuCode, isActiveSku, open, onCancel, onConfirm }: TypeProductDetailsModal) => {
  const navigate = useNavigate();

  const skuDetailQuery = useGetSkusSkuCodeQuery({
    skuCode,
    enabled: open && !isActiveSku,
  });

  const activeSkuDetailQuery = useGetActiveSkusSkuCodeQuery({
    skuCode,
    enabled: open && !!isActiveSku,
  });

  const productModalOptions = useProductModalOptions();

  const [productDetailModalValue, setProductDetailModalValue] = useProductStore(
    useShallow((state) => [state.productDetailModalValue, state.setProductDetailModalValue])
  );

  const detailData = isActiveSku ? activeSkuDetailQuery.data ?? null : skuDetailQuery.data ?? null;
  const hasTempData = !!(detailData && 'isTemp' in detailData && detailData.isTemp);

  const [detailModalInitialValue, setDetailModalInitialValue] =
    useState<TypeProductModalInput>(PRODUCT_MODAL_INITIAL_VALUE);

  useEffect(() => {
    if (detailData) {
      const { supplierList, skuCode, ...details } = detailData;

      const initialInputParams = {
        ...details,
        skuCode,
        supplierCodes:
          supplierList.map(({ supplierCode, supplierName }) => ({
            text: supplierName,
            value: supplierCode,
          })) ?? [],
        shelfLifeType:
          productModalOptions?.shelfLifeType.find(({ value }) => value === detailData.shelfLifeType) ?? null,
        temperatureType:
          productModalOptions?.keepLevel.find(({ value }) => value === detailData.temperatureType) ?? null,
        mainCategoryCode: {
          text: detailData.mainCategoryName,
          value: detailData.mainCategoryCode,
        },
        middleCategoryCode: {
          text: detailData.middleCategoryName,
          value: detailData.middleCategoryCode,
        },
        thirdCategoryCode: {
          text: detailData.thirdCategoryName,
          value: detailData.thirdCategoryCode,
        },
        fourthCategoryCode: {
          text: detailData.fourthCategoryName ?? '-',
          value: detailData.fourthCategoryCode ?? null,
        },
        unit: productModalOptions?.unit.find(({ value }) => value === detailData?.unit) ?? null,
        productType:
          productModalOptions?.productOperationType.find(({ value }) => value === detailData.productType) ?? null,
        kurlyOrderType:
          productModalOptions?.deliveryType.find(({ value }) => value === detailData.kurlyOrderType) ?? null,
        parcelOrderType:
          productModalOptions?.deliveryDocumentNum.find(({ value }) => value === detailData.parcelOrderType) ?? null,
        massDocumentNum:
          productModalOptions?.massDocumentNum.find(({ value }) => value === detailData.massDocumentNum) ?? null,
        kurlyKeepType:
          productModalOptions?.kurlyKeepType.find(({ value }) => value === detailData.kurlyKeepType) ?? null,
        deliveryDocumentNum:
          productModalOptions?.deliveryDispatchType.find(({ value }) => value === detailData.deliveryDocumentNum) ??
          null,
        packingDirection:
          productModalOptions?.packingDirection.find(({ value }) => value === detailData.packingDirection) ?? null,
        isFragile: detailData.isFragile ?? false,
        isWettable: detailData.isWettable ?? false,
        skuImageUrl: detailData.skuImageUrl ?? '',
        stockSafeDate: detailData.stockSafeDate?.toString() ?? null,
      };

      setProductDetailModalValue(initialInputParams);
      setDetailModalInitialValue(initialInputParams);
    }
  }, [detailData]);

  const handleClickCancelButton = () => {
    const isInputChanged = !isDeepEqual(productDetailModalValue, detailModalInitialValue);

    if (!isInputChanged) {
      onCancel();

      return;
    }

    dialogService.addDialog({
      children: (
        <>
          수정된 내용이 저장되지 않습니다.
          <br />
          페이지를 나가시겠습니까?
        </>
      ),
      open: true,
      type: 'confirm',
      cancelText: '취소',
      confirmText: '나가기',
      onConfirm: onCancel,
    });
  };

  const handleClickConfirmButton = () => {
    const isInputChanged = !isDeepEqual(productDetailModalValue, detailModalInitialValue);

    if (!isInputChanged) {
      onCancel();

      toastService.addToast({
        message: <>수정된 내용이 없습니다.</>,
      });

      return;
    }

    onConfirm();
  };

  const handleClickAdditionalModifyButton = () => {
    const currentSkuCode = detailData?.skuCode;

    if (!currentSkuCode) {
      toastService.addToast({
        type: 'error',
        message: TOAST_MESSAGE.DEFAULT_ERROR,
      });
      return;
    }

    onCancel();

    navigate(PATH.PRODUCT_REGISTER, {
      state: { targetSkuCode: currentSkuCode },
    });
  };

  return (
    <Modal
      open={open}
      title={
        <ProductDetailsModalTitle
          data={detailData}
          hasTempData={hasTempData}
          onClickModifyButton={handleClickAdditionalModifyButton}
        />
      }
      confirmText="저장"
      size="xlarge"
      onCancel={handleClickCancelButton}
      onConfirm={handleClickConfirmButton}
    >
      <div className={styles['product-modify-modal']}>
        <ProductDetailsModalDefaultSection skuCode={skuCode} isActiveSku={isActiveSku} />
        <ProductDetailsModalProductInfoSection hasTempData={hasTempData} />
        <ProductDetailsModalStockInfoSection hasTempData={hasTempData} />
      </div>
    </Modal>
  );
};

export default ProductDetailsModal;

/**
 * 상품 상세 모달 타이틀
 */
const ProductDetailsModalTitle = ({
  data,
  hasTempData,
  onClickModifyButton,
}: {
  data: (TypeSkuDetailData | TypeSkuDetailDataWithIsTemp) | null;
  hasTempData: boolean;
  onClickModifyButton: () => void;
}) => {
  const renderBadge = () => {
    let color: TypeBadgeProps['color'];

    if (data?.statusName === '등록 대기') {
      color = 'primary';
    } else if (data?.statusName === '등록 요청') {
      color = 'secondary';
    } else if (data?.statusName === '등록 완료') {
      color = 'info';
    }

    return (
      <Badge variant="light" color={color}>
        {data?.statusName.replaceAll(' ', '')}
      </Badge>
    );
  };

  return (
    <div className={styles['modal-title']}>
      <div className={styles['left-field']}>
        상품 상세
        {renderBadge()}
      </div>
      {hasTempData && (
        <div className={styles['right-field']}>
          <div>
            <ErrorFillIcon size={16} color="#FA622F" />
            <Text size="large" color="warning">
              이미 수정 요청한 상태입니다.
            </Text>
          </div>
          <Button
            size="medium"
            color="tertiary"
            variant="outlined"
            leftIcon={<EditSquareIcon size={16} />}
            onClick={onClickModifyButton}
          >
            추가 수정
          </Button>
        </div>
      )}
    </div>
  );
};
